import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import type { Subscription } from "../../Graphql/types/subscription";
import { useActivateSubscription } from "../../hooks/api/useSubscription";

interface AdminActivatePendingSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription | null;
  onSuccess?: () => void;
}

const AdminActivatePendingSubscriptionModal: React.FC<
  AdminActivatePendingSubscriptionModalProps
> = ({ open, onClose, subscription, onSuccess }) => {
  const activateSubscription = useActivateSubscription();

  const handleConfirmPaymentReceived = async () => {
    if (!subscription) return;
    try {
      await activateSubscription.mutateAsync({
        subscriptionId: subscription.subscriptionId,
      });
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by fetchGraphQL / setAlert
    }
  };

  const planLabel =
    subscription?.planName || subscription?.planId || "this plan";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Activate pending subscription
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.7 }}
        >
          Have you received payment for{" "}
          <Typography component="span" fontWeight="bold" color="text.primary">
            {planLabel}
          </Typography>
          ? Confirming will record the payment and activate the subscription.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          No, cancel
        </Button>
        <LoadingButton
          onClick={() => void handleConfirmPaymentReceived()}
          variant="contained"
          color="success"
          loading={activateSubscription.isPending}
        >
          Yes, payment received
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AdminActivatePendingSubscriptionModal;
